/**
 * Utility functions for multi-tenant routing
 */

const DEFAULT_DOMAIN = import.meta.env.VITE_DEFAULT_DOMAIN || 'privateprovider.com';

/**
 * Check if current URL is using default domain or standard dev environments
 */
const isDefaultOrDevDomain = (hostname: string): boolean => {
    return (
        hostname.includes(DEFAULT_DOMAIN) ||
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.azurewebsites.net') ||
        hostname.endsWith('.devtunnels.ms')
    );
};

export const SYSTEM_ROUTE_SEGMENTS = [
    'login',
    'dashboard',
    'tenant-dashboard',
    'contractor-dashboard',
    'home',
    'app',
    'apps',
    'contractors',
    'ppf',
    'settings',
    'services',
    'permitsandinspection',
    'permit&inspections',
    'municipality',
    'payments',
    'credits',
    'planreviews',
    'tasktemplates',
    'masteragreementtemplates',
    'states',
    'counties',
    'cities',
    'masteraddress',
    'roles',
    'users',
    'profiles',
    'profile',
    'jwt',
    'error',
    'no-permission',
    'forgot-password',
    'check-mail',
    'reset-password',
    'verify-otp',
    'register',
    'invalid-code',
    'esigncompleted',
    'esignsessionexpired',
    'generateinvoice'
];

/**
 * Check if a path segment is a reserved system route name
 */
export const isSystemRoute = (segment?: string): boolean => {
    if (!segment) return false;
    return SYSTEM_ROUTE_SEGMENTS.includes(segment.toLowerCase());
};

/**
 * Extract tenant keyword from current URL
 * Handles both subdomain and path-based routing
 * 
 * Examples:
 * - https://app.com/starproviders/login → "starproviders"
 * - https://www.starprovider.com/login → "www.starprovider.com"
 * - https://www.starprovider.com/app/login → "www.starprovider.com/app"
 * - https://www.star.smarterconstructions.com/permitrockstar/login → "www.star.smarterconstructions.com/permitrockstar"
 */
export const extractTenantKeyword = (url: string = window.location.href): string | null => {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        const pathname = urlObj.pathname;

        // Check if we're on the default domain
        const isDefaultDomain = isDefaultOrDevDomain(hostname);

        if (isDefaultDomain) {
            // Path-based tenant routing: /{uniqueCode}/login
            // Extract uniqueCode from path
            const pathSegments = pathname.split('/').filter(Boolean);
            if (pathSegments.length > 0) {
                const firstSegment = pathSegments[0];
                if (!isSystemRoute(firstSegment)) {
                    return firstSegment;
                }
            }
            return null;
        } else {
            // Custom domain routing
            // Return full domain + path (excluding system route segments)
            const pathSegments = pathname.split('/').filter(Boolean);
            const pathPart = pathSegments
                .filter(segment => !isSystemRoute(segment))
                .join('/');

            return pathPart ? `${hostname}/${pathPart}` : hostname;
        }
    } catch (error) {
        console.error('Error extracting tenant keyword:', error);
        return null;
    }
};

/**
 * Check if current URL is using custom domain (not default domain)
 */
export const isCustomDomain = (): boolean => {
    const hostname = window.location.hostname;
    return !isDefaultOrDevDomain(hostname);
};

/**
 * Save the tenant unique code to localStorage so that logout, 401s,
 * and page reloads can reliably identify the tenant context.
 */
export const saveTenantUniqueCode = (uniqueCode?: string | null): void => {
    try {
        if (uniqueCode) {
            localStorage.setItem('lastTenantUniqueCode', uniqueCode);
        }
    } catch (error) {
        console.error('Error saving tenant unique code:', error);
    }
};

/**
 * Clear tenant unique code from localStorage (e.g. for Super Admin)
 */
export const clearTenantUniqueCode = (): void => {
    try {
        localStorage.removeItem('lastTenantUniqueCode');
    } catch (error) {
        console.error('Error clearing tenant unique code:', error);
    }
};

/**
 * Retrieve the active or last known tenant uniqueCode from available sources
 */
export const getResolvedTenantUniqueCode = (uniqueCodeOverride?: string | null): string | null => {
    if (uniqueCodeOverride && typeof uniqueCodeOverride === 'string') {
        return uniqueCodeOverride;
    }

    // 1. Try URL extraction
    const fromUrl = extractTenantKeyword();
    if (fromUrl) {
        return fromUrl;
    }

    // 2. Try stored last known tenant code
    try {
        const storedCode = localStorage.getItem('lastTenantUniqueCode');
        if (storedCode) {
            return storedCode;
        }
    } catch {}

    // 3. Try stored tenant branding
    try {
        const storedBranding = localStorage.getItem('tenantBranding');
        if (storedBranding) {
            const parsed = JSON.parse(storedBranding);
            if (parsed?.uniqueCode) {
                return parsed.uniqueCode;
            }
        }
    } catch {}

    // 4. Try stored current user
    try {
        const storedUser = localStorage.getItem('currentUser');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser?.uniqueCode) {
                return parsedUser.uniqueCode;
            }
        }
    } catch {}

    return null;
};

/**
 * Get tenant-aware route path
 * @param route - Route path (e.g., '/dashboard', '/login')
 * @param uniqueCode - Optional unique code (if not provided, extracts from current URL)
 */
export const getTenantRoute = (route: string, uniqueCode?: string): string => {
    if (isCustomDomain()) {
        // Custom domain: routes are at root level
        return route.startsWith('/') ? route : `/${route}`;
    } else {
        // Default domain: routes include tenant code
        const code = getResolvedTenantUniqueCode(uniqueCode);
        if (code) {
            const cleanRoute = route.startsWith('/') ? route.slice(1) : route;
            return `/${code}/${cleanRoute}`;
        }
        // Fallback to super admin routes
        return route.startsWith('/') ? route : `/${route}`;
    }
};

/**
 * Get the proper login URL based on tenant context or fallback to Super Admin login
 */
export const getTenantLoginUrl = (uniqueCodeOverride?: string | null): string => {
    if (isCustomDomain()) {
        return '/login';
    }

    const resolvedCode = getResolvedTenantUniqueCode(uniqueCodeOverride);
    if (resolvedCode) {
        return `/${resolvedCode}/login`;
    }

    return '/login';
};

/**
 * Check if the current route is a super admin route (no tenant context)
 */
export const isSuperAdminRoute = (): boolean => {
    const keyword = extractTenantKeyword();
    return keyword === null;
};

